import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const nl = {
  hero: {
    badge: "Gratis · iOS & Android",
    h1: {
      prefix: "De",
      highlight: "vaccinaties",
      suffix: "van je gezin, altijd beschermd.",
    },
    sub: "Bewaar vaccinaties, krijg herinneringen en ontvang reisgezondheidsadvies voor ieder gezinslid, altijd bij de hand.",
    bullets: [
      "Vaccinaties bijhouden voor het hele gezin",
      "Reisadvies voor je bestemming",
      "Overzichten delen met artsen en scholen",
    ],
    phoneAlt: "Vaxy app op een smartphone",
  },
  stats: [
    {
      value: "200.000+",
      label: "Gebruikers vertrouwen ons",
      sub: "wereldwijd",
    },
    { value: "4,8", label: "App Store beoordeling", sub: "uit 3.000+ reviews" },
    {
      value: "230+",
      label: "Landen met reisadvies",
      sub: "op basis van LCR-richtlijnen",
    },
  ],
  partnersHeading:
    "Vertrouwd en erkend door toonaangevende gezondheidsorganisaties",
  why: {
    eyebrow: "Gezond ouder worden",
    heading: "Beschermd blijven, in elke levensfase",
    paragraph:
      "Griep, gordelroos en pneumokokken horen bij gezond ouder worden.\nVaxy bewaart wat je hebt gehad en herinnert je op tijd.",
    items: [
      {
        title: "Gezin",
        text: "Het RVP en het gele boekje, voor ieder profiel in huis.",
      },
      {
        title: "Onderweg",
        text: "Advies per bestemming, zodat je vertrekt met de juiste prikken.",
      },
      {
        title: "Later",
        text: "Een seintje voor griep, gordelroos of pneumokokken. Zodat je het niet zelf hoeft bij te houden.",
      },
    ],
  },
  features: {
    eyebrow: "Wat kun je met Vaxy?",
    heading: "Alles om goed beschermd te blijven",
    paragraph:
      "Bewaar vaccinaties, krijg herinneringen en bereid je eenvoudig voor op komende vaccinaties.",
    items: [
      {
        title: "Reisvaccinatieadvies",
        description:
          "Kies je bestemming, reizigers en vertrekdatum en ontvang persoonlijk vaccinatieadvies op basis van LCR-richtlijnen.",
      },
      {
        title: "Digitaal vaccinatieboekje",
        description:
          "Bewaar je vaccinatiehistorie veilig: scan foto's, upload documenten en houd vaccinaties en antistoftesten per persoon overzichtelijk.",
      },
      {
        title: "Gezin en gedeelde toegang",
        description:
          "Beheer vaccinaties voor kinderen of afhankelijken, nodig gezinsleden uit en deel veilige PDF-overzichten met scholen, werkgevers of zorgverleners.",
      },
      {
        title: "Herinneringen en meldingen",
        description:
          "Ontvang meldingen wanneer vaccinaties verlopen of gepland moeten worden en exporteer met één tik een compleet overzicht.",
      },
      {
        title: "Vind vaccinatiepunten",
        description:
          "Vind vaccinatieaanbieders in de buurt, bekijk beschikbaarheid en plan je afspraak op een plek en tijd die past.",
      },
    ],
  },
  travel: {
    eyebrow: "Reisgezondheid",
    heading: "Ga met vertrouwen op reis, waar je ook heen gaat",
    paragraph:
      "Vaxy is je digitale vaccinatieboekje voor thuis en onderweg. Krijg persoonlijk advies en mis nooit een belangrijke prik voor vertrek.",
    highlights: [
      {
        title: "Advies per bestemming",
        text: "Selecteer je bestemming en ontvang actueel reisgezondheidsadvies dat past bij je reis.",
      },
      {
        title: "Blijf beschermd",
        text: "Weet precies welke vaccinaties je nodig hebt voordat je vertrekt, gebaseerd op LCR-richtlijnen.",
      },
      {
        title: "Minder reisstress",
        text: "Bereid je ruim op tijd voor, zodat je met een gerust gevoel kunt genieten.",
      },
    ],
    card: {
      destination: "Bestemming",
      place: "Thailand",
      recommended: "Aanbevolen",
      count: "4 vaccinaties",
      vaccines: ["Hepatitis A", "Buiktyfus", "Tetanus", "Rabiës"],
      imageAlt: "Reisbestemming",
    },
  },
  howItWorks: {
    eyebrow: "Simpel en snel",
    heading: "Registreer een vaccinatie in 4 eenvoudige stappen",
    steps: [
      {
        number: "01",
        title: "Open de app",
        description: "Open Vaxy en kies je eigen profiel of dat van je kind.",
      },
      {
        number: "02",
        title: "Scan of voeg handmatig toe",
        description:
          "Scan je vaccinatieboekje of voeg een vaccinatie zelf toe.",
      },
      {
        number: "03",
        title: "Controleer en bevestig",
        description: "Bekijk de herkende gegevens en pas aan waar nodig.",
      },
      {
        number: "04",
        title: "Bewaar en ontvang herinneringen",
        description:
          "Klaar. Je vaccinatie staat veilig opgeslagen en je krijgt een melding wanneer het nodig is.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Wat mensen zeggen",
    heading: "Samen zorgen we dat niemand een prik mist",
    paragraph:
      "Van automatische herinneringen tot een digitaal vaccinatieboekje: Vaxy maakt beschermd blijven makkelijk voor het hele gezin.",
    items: [
      {
        name: "Rafael, 37",
        role: "Vader en sportcoach",
        quote:
          "Tussen werk en sport raak ik altijd dingen kwijt. Maar mijn vaccinaties en die van mijn kinderen staan veilig in Vaxy. Echt handig.",
      },
      {
        name: "Lotte, 23",
        role: "Geneeskundestudent",
        quote:
          "Voor mijn stage moest ik een overzicht met vaccinaties en titerbepalingen laten zien. Dankzij Vaxy had ik het met één tik geregeld.",
      },
      {
        name: "Sonja, 29",
        role: "Frequente reiziger",
        quote:
          "Ik reis veel en weet nooit precies welke vaccinaties ik nodig heb. Met Vaxy staat alles op één plek. Superpraktisch.",
      },
      {
        name: "Jeroen, 42",
        role: "HR-manager",
        quote:
          "Binnen onze organisatie gebruiken we Vaxy om collega's aan griepvaccinaties te herinneren. Heel praktisch en het scheelt veel mail en planning.",
      },
      {
        name: "Marieke, 34",
        role: "Moeder van twee",
        quote:
          "Eindelijk geen gedoe meer met het papieren boekje. Ik krijg gewoon een melding als mijn kinderen een vaccinatie nodig hebben. Dat geeft rust.",
      },
      {
        name: "Hans, 58",
        role: "Huisarts",
        quote:
          "Ik raad Vaxy aan mijn patiënten aan. Het voorkomt vergeten vaccinaties en maakt consulten veel efficiënter.",
      },
    ],
  },
  community: {
    eyebrow: "Word lid van onze community",
    heading: "Mensen die hun gezondheid op tijd serieus nemen",
    paragraph:
      "Sluit je aan bij een groeiende groep reizigers, ouders en professionals die geloven in voorkomen. Deel verhalen, ontdek reistips en blijf op de hoogte.",
    cta: "Gratis starten",
    proofCount: "1.000+",
    proof: "gezondheidsbewuste mensen",
  },
  download: {
    badge: "Beschikbaar op iOS & Android",
    heading: "Begin vandaag met je gezondheid beschermen",
    paragraph:
      "Download Vaxy gratis. Je digitale vaccinatieboekje, altijd in je broekzak.",
    note: "Eenvoudig, veilig en gratis om te starten.",
    iconAlt: "Vaxy app",
  },
};

export type HomeCopy = typeof nl;

const en: HomeCopy = {
  hero: {
    badge: "Free · iOS & Android",
    h1: {
      prefix: "Your family's",
      highlight: "vaccinations,",
      suffix: "always protected.",
    },
    sub: "Vaxy is a digital vaccination booklet. Keep records, booster reminders and travel health advice for every family member in one app.",
    bullets: [
      "Track vaccinations for the whole family",
      "Get travel health recommendations",
      "Share records with doctors & schools",
    ],
    phoneAlt: "Vaxy app on a smartphone",
  },
  stats: [
    { value: "200,000+", label: "Trusted users", sub: "across the globe" },
    { value: "4.8", label: "App Store rating", sub: "from 3,000+ reviews" },
    {
      value: "230+",
      label: "Countries with travel advice",
      sub: "based on LCR guidance",
    },
  ],
  partnersHeading: "Trusted & recognised by leading health organisations",
  why: {
    eyebrow: "Healthy ageing",
    heading: "Stay protected, at every age",
    paragraph:
      "Flu, shingles and pneumococcal shots are part of healthy ageing.\nVaxy stores what you have had and reminds you when it is time.",
    items: [
      {
        title: "Family",
        text: "Childhood programmes and the Yellow Booklet, for every profile at home.",
      },
      {
        title: "On the road",
        text: "Advice per destination, so you leave with the right vaccinations.",
      },
      {
        title: "Later on",
        text: "A nudge for flu, shingles or pneumococcal. So you do not have to keep track yourself.",
      },
    ],
  },
  features: {
    eyebrow: "What can you do with Vaxy?",
    heading: "Everything you need to stay protected",
    paragraph:
      "Store vaccination records, get reminders, and prepare easily for upcoming vaccinations.",
    items: [
      {
        title: "Travel Vaccination Advice",
        description:
          "Choose your destination, travellers and departure date to receive personalised vaccination recommendations based on LCR (Dutch travel health) guidance.",
      },
      {
        title: "Digital Vaccination Records",
        description:
          "Securely store your vaccination history — scan photos, upload documents and keep vaccinations and antibody test results organised per person.",
      },
      {
        title: "Family & Shared Access",
        description:
          "Manage vaccinations for children or dependants, invite family members and securely share PDF summaries with schools, employers or healthcare providers.",
      },
      {
        title: "Reminders & Alerts",
        description:
          "Receive notifications when vaccinations are due or expiring and export a complete vaccination overview with one tap.",
      },
      {
        title: "Find Vaccination Providers",
        description:
          "Find nearby vaccination providers, check availability and plan your appointment at a convenient time and location.",
      },
    ],
  },
  travel: {
    eyebrow: "Travel Health",
    heading: "Travel with confidence, wherever you go",
    paragraph:
      "Vaxy is your digital vaccination record and travel health companion. Get personalised recommendations and never miss an important vaccination before your trip.",
    highlights: [
      {
        title: "Destination-based advice",
        text: "Select your destination and receive up-to-date travel health advice tailored to your trip.",
      },
      {
        title: "Stay protected",
        text: "Know exactly which vaccinations you need before you travel, based on LCR (Dutch travel health) guidance.",
      },
      {
        title: "Reduce travel stress",
        text: "Prepare well in advance so you can focus on enjoying your trip, worry-free.",
      },
    ],
    card: {
      destination: "Destination",
      place: "Thailand",
      recommended: "Recommended",
      count: "4 vaccinations",
      vaccines: ["Hepatitis A", "Typhoid", "Tetanus", "Rabies"],
      imageAlt: "Travel destination",
    },
  },
  howItWorks: {
    eyebrow: "Simple & fast",
    heading: "Register a vaccination in 4 easy steps",
    steps: [
      {
        number: "01",
        title: "Open the app",
        description:
          "Open Vaxy and choose your own profile or that of your child.",
      },
      {
        number: "02",
        title: "Scan or add manually",
        description:
          "Scan your vaccination booklet or add a vaccination manually.",
      },
      {
        number: "03",
        title: "Review and confirm",
        description: "Check the recognised data and adjust if necessary.",
      },
      {
        number: "04",
        title: "Save & get reminders",
        description:
          "Done. Your vaccination is now safely stored and you'll be notified when it's due.",
      },
    ],
  },
  testimonials: {
    eyebrow: "What people say",
    heading: "Together we make sure nobody misses a jab",
    paragraph:
      "From automatic reminders to a digital vaccination record, Vaxy makes it easy for the whole family to stay protected.",
    items: [
      {
        name: "Rafael, 37",
        role: "Father & Sports Coach",
        quote:
          "Between work and sports I'm always losing track of things. But my vaccinations and those of my kids are always safely stored in Vaxy. So convenient.",
      },
      {
        name: "Lotte, 23",
        role: "Medical Student",
        quote:
          "For my internship I had to show a vaccination overview with titre tests. Thanks to Vaxy I had it sorted in one tap.",
      },
      {
        name: "Sonja, 29",
        role: "Frequent Traveller",
        quote:
          "I travel abroad a lot and never know which vaccinations I need. With Vaxy I have everything in one place. Super handy.",
      },
      {
        name: "Jeroen, 42",
        role: "HR Manager",
        quote:
          "In our organisation we use Vaxy to remind staff about flu vaccinations. Very practical and saves us a lot of emails and planning.",
      },
      {
        name: "Marieke, 34",
        role: "Mother of Two",
        quote:
          "Finally no more hassle with the paper booklet. I just get a notification when my kids need a vaccination. Gives me so much peace of mind.",
      },
      {
        name: "Hans, 58",
        role: "General Practitioner",
        quote:
          "I recommend Vaxy to my patients. It prevents people from forgetting their vaccinations and makes consultations much more efficient.",
      },
    ],
  },
  community: {
    eyebrow: "Join Our Community",
    heading: "People who take control of their health before it's too late",
    paragraph:
      "Join a growing community of health-conscious travellers, parents and professionals who believe in prevention over cure. Share stories, discover travel tips, and stay informed.",
    cta: "Get Started Free",
    proofCount: "1,000+",
    proof: "health-conscious people",
  },
  download: {
    badge: "Available on iOS & Android",
    heading: "Start protecting your health today",
    paragraph:
      "Download Vaxy for free. Your digital vaccination record and travel health companion — always in your pocket.",
    note: "Simple, secure, and free to start.",
    iconAlt: "Vaxy App",
  },
};

const de: HomeCopy = {
  hero: {
    badge: "Kostenlos · iOS & Android",
    h1: {
      prefix: "Die",
      highlight: "Impfungen",
      suffix: "deiner Familie, immer geschützt.",
    },
    sub: "Speichere Impfungen, erhalte Erinnerungen und Reisegesundheitsberatung für jedes Familienmitglied, immer griffbereit.",
    bullets: [
      "Impfungen für die ganze Familie verwalten",
      "Empfehlungen für Reisegesundheit erhalten",
      "Nachweise mit Ärzten und Schulen teilen",
    ],
    phoneAlt: "Vaxy App auf einem Smartphone",
  },
  stats: [
    { value: "200.000+", label: "Nutzer vertrauen uns", sub: "weltweit" },
    {
      value: "4,8",
      label: "App-Store-Bewertung",
      sub: "aus 3.000+ Rezensionen",
    },
    {
      value: "230+",
      label: "Länder mit Reiseberatung",
      sub: "auf Basis der LCR-Richtlinien",
    },
  ],
  partnersHeading:
    "Vertraut und anerkannt von führenden Gesundheitsorganisationen",
  why: {
    eyebrow: "Gesund älter werden",
    heading: "Ein Leben lang gut geschützt",
    paragraph:
      "Grippe, Gürtelrose und Pneumokokken gehören zum gesunden Älterwerden.\nVaxy speichert, was du schon hattest, und erinnert dich rechtzeitig.",
    items: [
      {
        title: "Familie",
        text: "Das Impfprogramm und das gelbe Heft, für jedes Profil zu Hause.",
      },
      {
        title: "Unterwegs",
        text: "Empfehlungen pro Reiseziel, damit du mit den richtigen Impfungen aufbrichst.",
      },
      {
        title: "Später",
        text: "Ein Hinweis für Grippe, Gürtelrose oder Pneumokokken. Damit du es nicht selbst im Kopf behalten musst.",
      },
    ],
  },
  features: {
    eyebrow: "Was kannst du mit Vaxy tun?",
    heading: "Alles, was du brauchst, um geschützt zu bleiben",
    paragraph:
      "Speichere Impfungen, erhalte Erinnerungen und bereite dich einfach auf kommende Impfungen vor.",
    items: [
      {
        title: "Reiseimpfberatung",
        description:
          "Wähle Ziel, Reisende und Abreisedatum und erhalte persönliche Impfempfehlungen auf Basis der LCR-Richtlinien.",
      },
      {
        title: "Digitale Impfnachweise",
        description:
          "Speichere deine Impfgeschichte sicher: Fotos scannen, Dokumente hochladen und Impfungen sowie Antikörpertests pro Person ordnen.",
      },
      {
        title: "Familie und gemeinsamer Zugriff",
        description:
          "Verwalte Impfungen für Kinder oder Angehörige, lade Familienmitglieder ein und teile sichere PDF-Übersichten mit Schulen, Arbeitgebern oder Ärzten.",
      },
      {
        title: "Erinnerungen und Hinweise",
        description:
          "Erhalte Benachrichtigungen, wenn Impfungen fällig sind oder ablaufen, und exportiere mit einem Fingertipp eine vollständige Übersicht.",
      },
      {
        title: "Impfstellen finden",
        description:
          "Finde Impfstellen in deiner Nähe, prüfe Verfügbarkeit und plane deinen Termin passend zu Ort und Zeit.",
      },
    ],
  },
  travel: {
    eyebrow: "Reisegesundheit",
    heading: "Reise mit Vertrauen, wohin du auch gehst",
    paragraph:
      "Vaxy ist dein digitaler Impfpass und Begleiter für Reisegesundheit. Erhalte persönliche Empfehlungen und verpasse vor deiner Reise keine wichtige Impfung.",
    highlights: [
      {
        title: "Beratung nach Reiseziel",
        text: "Wähle dein Reiseziel und erhalte aktuelle Reisegesundheitsinformationen passend zu deiner Reise.",
      },
      {
        title: "Geschützt bleiben",
        text: "Wisse genau, welche Impfungen du vor der Reise brauchst, basierend auf den LCR-Richtlinien.",
      },
      {
        title: "Weniger Reisestress",
        text: "Bereite dich frühzeitig vor, damit du deine Reise sorgenfrei genießen kannst.",
      },
    ],
    card: {
      destination: "Reiseziel",
      place: "Thailand",
      recommended: "Empfohlen",
      count: "4 Impfungen",
      vaccines: ["Hepatitis A", "Typhus", "Tetanus", "Tollwut"],
      imageAlt: "Reiseziel",
    },
  },
  howItWorks: {
    eyebrow: "Einfach und schnell",
    heading: "Eine Impfung in 4 einfachen Schritten registrieren",
    steps: [
      {
        number: "01",
        title: "App öffnen",
        description: "Öffne Vaxy und wähle dein Profil oder das deines Kindes.",
      },
      {
        number: "02",
        title: "Scannen oder manuell hinzufügen",
        description:
          "Scanne deinen Impfpass oder füge eine Impfung manuell hinzu.",
      },
      {
        number: "03",
        title: "Prüfen und bestätigen",
        description:
          "Kontrolliere die erkannten Daten und passe sie bei Bedarf an.",
      },
      {
        number: "04",
        title: "Speichern und Erinnerungen erhalten",
        description:
          "Fertig. Deine Impfung ist sicher gespeichert und du wirst rechtzeitig erinnert.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Was Menschen sagen",
    heading: "Gemeinsam sorgen wir dafür, dass niemand eine Impfung verpasst",
    paragraph:
      "Von automatischen Erinnerungen bis zum digitalen Impfpass: Vaxy macht es der ganzen Familie leicht, geschützt zu bleiben.",
    items: [
      {
        name: "Rafael, 37",
        role: "Vater und Sporttrainer",
        quote:
          "Zwischen Arbeit und Sport verliere ich ständig den Überblick. Aber meine Impfungen und die meiner Kinder sind in Vaxy sicher gespeichert. Sehr praktisch.",
      },
      {
        name: "Lotte, 23",
        role: "Medizinstudentin",
        quote:
          "Für mein Praktikum musste ich eine Impf- und Titerübersicht zeigen. Mit Vaxy hatte ich sie mit einem Fingertipp bereit.",
      },
      {
        name: "Sonja, 29",
        role: "Vielreisende",
        quote:
          "Ich reise viel und weiß nie genau, welche Impfungen ich brauche. Mit Vaxy habe ich alles an einem Ort. Super hilfreich.",
      },
      {
        name: "Jeroen, 42",
        role: "HR-Manager",
        quote:
          "In unserer Organisation nutzen wir Vaxy, um Mitarbeitende an Grippeimpfungen zu erinnern. Sehr praktisch und es spart viele E-Mails und Planung.",
      },
      {
        name: "Marieke, 34",
        role: "Mutter von zwei Kindern",
        quote:
          "Endlich kein Stress mehr mit dem Papierheft. Ich bekomme einfach eine Nachricht, wenn meine Kinder eine Impfung brauchen. Das beruhigt.",
      },
      {
        name: "Hans, 58",
        role: "Hausarzt",
        quote:
          "Ich empfehle Vaxy meinen Patienten. Es verhindert vergessene Impfungen und macht Beratungsgespräche deutlich effizienter.",
      },
    ],
  },
  community: {
    eyebrow: "Werde Teil unserer Community",
    heading:
      "Menschen, die ihre Gesundheit rechtzeitig selbst in die Hand nehmen",
    paragraph:
      "Schließe dich einer wachsenden Community aus Reisenden, Eltern und Fachleuten an, die an Vorbeugung glauben. Teile Erfahrungen, entdecke Reisetipps und bleib informiert.",
    cta: "Kostenlos starten",
    proofCount: "1.000+",
    proof: "gesundheitsbewusste Menschen",
  },
  download: {
    badge: "Verfügbar für iOS & Android",
    heading: "Beginne heute, deine Gesundheit zu schützen",
    paragraph:
      "Lade Vaxy kostenlos herunter. Dein digitaler Impfpass und Begleiter für Reisegesundheit, immer in deiner Tasche.",
    note: "Einfach, sicher und kostenlos zum Start.",
    iconAlt: "Vaxy App",
  },
};

const fr: HomeCopy = {
  hero: {
    badge: "Gratuit · iOS & Android",
    h1: {
      prefix: "Les",
      highlight: "vaccins",
      suffix: "de votre famille, toujours protégés.",
    },
    sub: "Enregistrez vos vaccins, recevez des rappels et obtenez des conseils santé voyage pour chaque membre de la famille, toujours dans votre poche.",
    bullets: [
      "Suivre les vaccins de toute la famille",
      "Recevoir des recommandations santé voyage",
      "Partager les dossiers avec médecins et écoles",
    ],
    phoneAlt: "Application Vaxy sur un smartphone",
  },
  stats: [
    {
      value: "200 000+",
      label: "Utilisateurs confiants",
      sub: "dans le monde",
    },
    { value: "4,8", label: "Note sur l'App Store", sub: "sur 3 000+ avis" },
    {
      value: "230+",
      label: "Pays avec conseil voyage",
      sub: "selon les recommandations LCR",
    },
  ],
  partnersHeading: "Reconnu par des organisations de santé de premier plan",
  why: {
    eyebrow: "Bien vieillir",
    heading: "Rester protégé, à tout âge",
    paragraph:
      "Grippe, zona et pneumocoque font partie du bien-vieillir.\nVaxy garde ce que vous avez déjà reçu et vous rappelle au bon moment.",
    items: [
      {
        title: "Famille",
        text: "Le calendrier vaccinal et le carnet jaune, pour chaque profil à la maison.",
      },
      {
        title: "En voyage",
        text: "Des conseils par destination, pour partir avec les bons vaccins.",
      },
      {
        title: "Plus tard",
        text: "Un rappel pour la grippe, le zona ou le pneumocoque. Pour ne pas avoir à tout retenir soi-même.",
      },
    ],
  },
  features: {
    eyebrow: "Que pouvez-vous faire avec Vaxy ?",
    heading: "Tout pour rester bien protégé",
    paragraph:
      "Enregistrez vos vaccins, recevez des rappels et préparez facilement vos prochaines vaccinations.",
    items: [
      {
        title: "Conseils de vaccination voyage",
        description:
          "Choisissez votre destination, les voyageurs et la date de départ pour recevoir des recommandations personnalisées selon les directives LCR."
      },
      {
        title: "Carnet de vaccination numérique",
        description:
          "Conservez votre historique vaccinal en sécurité : scannez des photos, importez des documents et organisez vaccins et tests d'anticorps par personne.",
      },
      {
        title: "Famille et accès partagé",
        description:
          "Gérez les vaccins des enfants ou proches, invitez des membres de la famille et partagez des PDF sécurisés avec écoles, employeurs ou soignants.",
      },
      {
        title: "Rappels et alertes",
        description:
          "Recevez des notifications quand des vaccins arrivent à échéance ou doivent être planifiés, puis exportez un aperçu complet en un geste.",
      },
      {
        title: "Trouver des centres de vaccination",
        description:
          "Trouvez des prestataires proches, vérifiez les disponibilités et planifiez votre rendez-vous au bon endroit et au bon moment.",
      },
    ],
  },
  travel: {
    eyebrow: "Santé voyage",
    heading: "Voyagez sereinement, où que vous alliez",
    paragraph:
      "Vaxy est votre carnet vaccinal numérique et compagnon santé voyage. Recevez des recommandations personnalisées et ne manquez jamais un vaccin important avant le départ.",
    highlights: [
      {
        title: "Conseils par destination",
        text: "Sélectionnez votre destination et recevez des conseils santé voyage à jour, adaptés à votre itinéraire.",
      },
      {
        title: "Rester protégé",
        text: "Sachez exactement quels vaccins sont nécessaires avant de partir, selon les recommandations LCR.",
      },
      {
        title: "Moins de stress avant le départ",
        text: "Préparez-vous à temps pour profiter de votre voyage l'esprit tranquille.",
      },
    ],
    card: {
      destination: "Destination",
      place: "Thaïlande",
      recommended: "Recommandé",
      count: "4 vaccins",
      vaccines: ["Hépatite A", "Typhoïde", "Tétanos", "Rage"],
      imageAlt: "Destination de voyage",
    },
  },
  howItWorks: {
    eyebrow: "Simple et rapide",
    heading: "Enregistrez un vaccin en 4 étapes simples",
    steps: [
      {
        number: "01",
        title: "Ouvrir l'application",
        description:
          "Ouvrez Vaxy et choisissez votre profil ou celui de votre enfant.",
      },
      {
        number: "02",
        title: "Scanner ou ajouter à la main",
        description:
          "Scannez votre carnet de vaccination ou ajoutez un vaccin manuellement.",
      },
      {
        number: "03",
        title: "Vérifier et confirmer",
        description:
          "Contrôlez les données reconnues et corrigez-les si nécessaire.",
      },
      {
        number: "04",
        title: "Enregistrer et recevoir des rappels",
        description:
          "C'est fait. Votre vaccin est enregistré en sécurité et vous serez prévenu au bon moment.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Ce qu'en disent les utilisateurs",
    heading: "Ensemble, nous évitons les vaccins oubliés",
    paragraph:
      "Des rappels automatiques au carnet vaccinal numérique, Vaxy aide toute la famille à rester protégée.",
    items: [
      {
        name: "Rafael, 37",
        role: "Père et coach sportif",
        quote:
          "Entre le travail et le sport, je perds vite le fil. Mais mes vaccins et ceux de mes enfants sont toujours bien rangés dans Vaxy. Tellement pratique.",
      },
      {
        name: "Lotte, 23",
        role: "Étudiante en médecine",
        quote:
          "Pour mon stage, je devais présenter un aperçu de mes vaccins et dosages d'anticorps. Avec Vaxy, tout était prêt en un geste.",
      },
      {
        name: "Sonja, 29",
        role: "Grande voyageuse",
        quote:
          "Je voyage beaucoup et je ne sais jamais quels vaccins il me faut. Avec Vaxy, tout est au même endroit. Vraiment pratique.",
      },
      {
        name: "Jeroen, 42",
        role: "Responsable RH",
        quote:
          "Dans notre organisation, nous utilisons Vaxy pour rappeler la vaccination contre la grippe. C'est pratique et cela évite beaucoup d'e-mails.",
      },
      {
        name: "Marieke, 34",
        role: "Mère de deux enfants",
        quote:
          "Enfin fini les soucis avec le carnet papier. Je reçois simplement une notification quand mes enfants ont besoin d'un vaccin. Ça rassure.",
      },
      {
        name: "Hans, 58",
        role: "Médecin généraliste",
        quote:
          "Je recommande Vaxy à mes patients. Cela évite les vaccins oubliés et rend les consultations beaucoup plus efficaces.",
      },
    ],
  },
  community: {
    eyebrow: "Rejoignez notre communauté",
    heading: "Des personnes qui prennent leur santé en main à temps",
    paragraph:
      "Rejoignez une communauté grandissante de voyageurs, parents et professionnels qui croient en la prévention. Partagez des expériences, découvrez des conseils voyage et restez informé.",
    cta: "Commencer gratuitement",
    proofCount: "1 000+",
    proof: "personnes attentives à leur santé",
  },
  download: {
    badge: "Disponible sur iOS & Android",
    heading: "Commencez à protéger votre santé dès aujourd'hui",
    paragraph:
      "Téléchargez Vaxy gratuitement. Votre carnet vaccinal numérique et compagnon santé voyage, toujours dans votre poche.",
    note: "Simple, sûr et gratuit pour commencer.",
    iconAlt: "Application Vaxy",
  },
};

const es: HomeCopy = {
  hero: {
    badge: "Gratis · iOS y Android",
    h1: {
      prefix: "Las",
      highlight: "vacunas",
      suffix: "de tu familia, siempre protegidas.",
    },
    sub: "Guarda registros de vacunación, recibe recordatorios y consejos de salud para viajar para cada miembro de tu familia, siempre en el bolsillo.",
    bullets: [
      "Controla las vacunas de toda la familia",
      "Recibe recomendaciones de salud para viajar",
      "Comparte registros con médicos y escuelas",
    ],
    phoneAlt: "App Vaxy en un smartphone",
  },
  stats: [
    {
      value: "200.000+",
      label: "Usuarios que confían",
      sub: "en todo el mundo",
    },
    {
      value: "4,8",
      label: "Valoración en App Store",
      sub: "de 3.000+ reseñas",
    },
    {
      value: "230+",
      label: "Países con consejo de viaje",
      sub: "según las directrices LCR",
    },
  ],
  partnersHeading:
    "Con la confianza y el reconocimiento de organizaciones sanitarias líderes",
  why: {
    eyebrow: "Envejecer con salud",
    heading: "Protegido a cualquier edad",
    paragraph:
      "Gripe, herpes zóster y neumococo forman parte de envejecer con salud.\nVaxy guarda lo que ya tienes y te avisa a tiempo.",
    items: [
      {
        title: "Familia",
        text: "El calendario infantil y la cartilla amarilla, para cada perfil en casa.",
      },
      {
        title: "De viaje",
        text: "Consejo por destino, para salir con las vacunas adecuadas.",
      },
      {
        title: "Más adelante",
        text: "Un aviso para la gripe, el herpes zóster o el neumococo. Para no tener que recordarlo tú.",
      },
    ],
  },
  features: {
    eyebrow: "¿Qué puedes hacer con Vaxy?",
    heading: "Todo lo que necesitas para seguir protegido",
    paragraph:
      "Guarda tus vacunas, recibe recordatorios y prepárate fácilmente para próximas vacunaciones.",
    items: [
      {
        title: "Consejos de vacunas para viajar",
        description:
          "Elige destino, viajeros y fecha de salida para recibir recomendaciones personalizadas según las directrices LCR.",
      },
      {
        title: "Registros de vacunación digitales",
        description:
          "Guarda tu historial de forma segura: escanea fotos, sube documentos y organiza vacunas y pruebas de anticuerpos por persona.",
      },
      {
        title: "Familia y acceso compartido",
        description:
          "Gestiona vacunas de hijos o personas a cargo, invita a familiares y comparte resúmenes PDF seguros con escuelas, empresas o profesionales sanitarios.",
      },
      {
        title: "Recordatorios y alertas",
        description:
          "Recibe avisos cuando las vacunas caduquen o estén pendientes y exporta un resumen completo con un toque.",
      },
      {
        title: "Encuentra centros de vacunación",
        description:
          "Busca proveedores cercanos, consulta disponibilidad y planifica tu cita en el momento y lugar que mejor te venga.",
      },
    ],
  },
  travel: {
    eyebrow: "Salud en viajes",
    heading: "Viaja con confianza, vayas donde vayas",
    paragraph:
      "Vaxy es tu cartilla de vacunación digital y compañera de salud para viajar. Recibe recomendaciones personalizadas y no olvides ninguna vacuna importante antes del viaje.",
    highlights: [
      {
        title: "Consejos por destino",
        text: "Selecciona tu destino y recibe consejos de salud actualizados y adaptados a tu viaje.",
      },
      {
        title: "Mantente protegido",
        text: "Sabe exactamente qué vacunas necesitas antes de viajar, según las directrices LCR.",
      },
      {
        title: "Reduce el estrés del viaje",
        text: "Prepárate con tiempo para centrarte en disfrutar sin preocupaciones.",
      },
    ],
    card: {
      destination: "Destino",
      place: "Tailandia",
      recommended: "Recomendado",
      count: "4 vacunas",
      vaccines: ["Hepatitis A", "Tifoidea", "Tétanos", "Rabia"],
      imageAlt: "Destino de viaje",
    },
  },
  howItWorks: {
    eyebrow: "Simple y rápido",
    heading: "Registra una vacuna en 4 pasos sencillos",
    steps: [
      {
        number: "01",
        title: "Abre la app",
        description: "Abre Vaxy y elige tu perfil o el de tu hijo.",
      },
      {
        number: "02",
        title: "Escanea o añade manualmente",
        description:
          "Escanea tu cartilla de vacunación o añade una vacuna a mano.",
      },
      {
        number: "03",
        title: "Revisa y confirma",
        description:
          "Comprueba los datos reconocidos y corrige lo que haga falta.",
      },
      {
        number: "04",
        title: "Guarda y recibe recordatorios",
        description:
          "Listo. Tu vacuna queda guardada de forma segura y recibirás avisos cuando toque.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Lo que dice la gente",
    heading: "Juntos evitamos que nadie olvide una vacuna",
    paragraph:
      "Desde recordatorios automáticos hasta una cartilla digital, Vaxy facilita que toda la familia siga protegida.",
    items: [
      {
        name: "Rafael, 37",
        role: "Padre y entrenador deportivo",
        quote:
          "Entre el trabajo y el deporte siempre pierdo la pista de todo. Pero mis vacunas y las de mis hijos están seguras en Vaxy. Muy cómodo.",
      },
      {
        name: "Lotte, 23",
        role: "Estudiante de medicina",
        quote:
          "Para mis prácticas tenía que mostrar un resumen de vacunas y títulos de anticuerpos. Con Vaxy lo tuve listo con un toque.",
      },
      {
        name: "Sonja, 29",
        role: "Viajera frecuente",
        quote:
          "Viajo mucho y nunca sé qué vacunas necesito. Con Vaxy lo tengo todo en un solo lugar. Súper útil.",
      },
      {
        name: "Jeroen, 42",
        role: "Responsable de RR. HH.",
        quote:
          "En nuestra organización usamos Vaxy para recordar al personal la vacuna de la gripe. Es práctico y ahorra muchos correos y planificación.",
      },
      {
        name: "Marieke, 34",
        role: "Madre de dos hijos",
        quote:
          "Por fin se acabó el lío de la cartilla en papel. Recibo una notificación cuando mis hijos necesitan una vacuna. Me da mucha tranquilidad.",
      },
      {
        name: "Hans, 58",
        role: "Médico de familia",
        quote:
          "Recomiendo Vaxy a mis pacientes. Evita olvidos y hace que las consultas sean mucho más eficientes.",
      },
    ],
  },
  community: {
    eyebrow: "Únete a nuestra comunidad",
    heading: "Personas que toman las riendas de su salud a tiempo",
    paragraph:
      "Únete a una comunidad creciente de viajeros, padres y profesionales que creen en la prevención. Comparte historias, descubre consejos de viaje y mantente informado.",
    cta: "Empezar gratis",
    proofCount: "1.000+",
    proof: "personas conscientes de su salud",
  },
  download: {
    badge: "Disponible en iOS y Android",
    heading: "Empieza hoy a proteger tu salud",
    paragraph:
      "Descarga Vaxy gratis. Tu cartilla de vacunación digital y compañera de salud para viajar, siempre en el bolsillo.",
    note: "Simple, seguro y gratis para empezar.",
    iconAlt: "App Vaxy",
  },
};

export const home: Record<Locale, HomeCopy> = copy(nl, en, de, fr, es);
